import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Gift, Heart } from "lucide-react";
import { PortalHeader, PortalFooter } from "./NassauToday";
import EntryFields, { useEntrant, ThankYou, pInput } from "../components/promo/EntryFields";
import ImageUpload from "../components/ImageUpload";
import { getCampaign, enterCampaign, submitContestPhoto, voteContestPhoto, TYPE_META } from "../lib/promotions";
import { apiError } from "../lib/member";
import { TYPE_ICON } from "./Contests";

export default function ContestDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = () => getCampaign(id).then(setC).catch(() => setMissing(true));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const Icon = TYPE_ICON[c?.type] || Gift;
  return (
    <div className="portal" data-testid="contest-detail-page">
      <PortalHeader />
      <main className="mx-auto max-w-4xl px-4 py-6">
        <Link to="/contests" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500"><ChevronLeft size={16} /> All contests</Link>
        {missing && <p className="py-16 text-center text-slate-500">This campaign isn't available.</p>}
        {c && (
          <>
            <section className="overflow-hidden rounded-3xl bg-white ring-1 ring-slate-200">
              {c.image && <img src={c.image} alt={c.title} className={`w-full object-cover ${c.type === "photo" ? "h-72 object-[center_20%] sm:h-[28rem]" : "h-56 sm:h-80"}`} />}
              <div className="p-6 sm:p-8">
                <p className="portal-chip flex items-center gap-1.5 text-[var(--p-clay)]"><Icon size={13} /> {TYPE_META[c.type]?.label}{c.sponsor_name && ` · Presented by ${c.sponsor_name}`}</p>
                <h1 className="font-portal mt-2 text-3xl font-black tracking-tight text-[var(--p-ink)] sm:text-4xl" data-testid="contest-title">{c.title}</h1>
                {c.prize && <p className="mt-2 flex items-center gap-1.5 font-semibold text-[var(--p-teal)]"><Gift size={16} /> {c.prize}</p>}
                <p className="mt-3 text-slate-600">{c.description}</p>
                {c.end_date && <p className="mt-2 text-xs text-slate-400">Ends {c.end_date}</p>}
              </div>
            </section>
            <div className="mt-6">
              {c.type === "sweepstakes" && <SimpleEntry c={c} />}
              {c.type === "quiz" && <Quiz c={c} />}
              {c.type === "ballot" && <Ballot c={c} />}
              {c.type === "photo" && <PhotoContest c={c} reload={load} />}
            </div>
          </>
        )}
      </main>
      <PortalFooter />
    </div>
  );
}

function useSubmit(fn) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async (...a) => { setBusy(true); try { setRes(await fn(...a)); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  return [run, busy, res, setRes];
}

const SubmitBtn = ({ busy, label, testid }) => (
  <button type="submit" disabled={busy} data-testid={testid} className="w-full rounded-full bg-[var(--p-teal)] py-3 text-sm font-bold text-white hover:bg-[var(--p-teal-bright)] disabled:opacity-60">{busy ? "Sending…" : label}</button>
);

function SimpleEntry({ c }) {
  const [f, setF, member] = useEntrant();
  const [run, busy, res] = useSubmit((d) => enterCampaign(c.id, d));
  if (res) return <ThankYou res={res} />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); run(f); }} className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="sweepstakes-form">
      <h2 className="font-portal text-xl font-extrabold text-[var(--p-ink)]">Enter to win{c.entry_frequency === "daily" && <span className="ml-2 text-xs font-semibold text-slate-400">One entry per day</span>}</h2>
      <EntryFields c={c} f={f} setF={setF} member={member} />
      <SubmitBtn busy={busy} label="Enter now" testid="sweepstakes-submit" />
    </form>
  );
}

function Quiz({ c }) {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState({});
  const [f, setF, member] = useEntrant();
  const [run, busy, res] = useSubmit((d) => enterCampaign(c.id, d));
  const qs = c.questions || [];
  if (res) {
    return (
      <div className="space-y-4" data-testid="quiz-result">
        <div className="rounded-3xl bg-[var(--p-ink)] p-8 text-center text-white">
          {res.result && <><p className="portal-chip text-[var(--p-amber)]">You are…</p><p className="font-portal mt-2 text-4xl font-black" data-testid="quiz-result-title">{res.result.title}</p><p className="mx-auto mt-3 max-w-md text-white/80">{res.result.description}</p></>}
          {res.total !== undefined && <><p className="portal-chip text-[var(--p-amber)]">Your score</p><p className="font-portal mt-2 text-5xl font-black" data-testid="quiz-score">{res.score} / {res.total}</p></>}
        </div>
        <ThankYou res={res} />
      </div>
    );
  }
  if (step < qs.length) {
    const q = qs[step];
    return (
      <div className="rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="quiz-player">
        <div className="mb-4 h-1.5 rounded-full bg-slate-100"><div className="h-full rounded-full bg-[var(--p-teal)] transition-all" style={{ width: `${(step / qs.length) * 100}%` }} /></div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Question {step + 1} of {qs.length}</p>
        <p className="font-portal mt-1 text-2xl font-extrabold text-[var(--p-ink)]" data-testid="quiz-question">{q.text}</p>
        <div className="mt-5 grid gap-2">
          {q.answers.map((a) => (
            <button key={a.id} onClick={() => { setPicks({ ...picks, [q.id]: a.id }); setStep(step + 1); }} data-testid={`quiz-answer-${a.id}`}
              className="rounded-2xl border border-slate-200 px-4 py-3.5 text-left font-semibold text-[var(--p-ink)] transition-colors hover:border-[var(--p-teal)] hover:bg-[var(--p-teal)]/5">{a.text}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); run({ ...f, quiz_answers: picks }); }} className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="quiz-entry-form">
      <h2 className="font-portal text-xl font-extrabold text-[var(--p-ink)]">Almost there! Enter your details to see your {c.quiz_kind === "trivia" ? "score" : "result"}.</h2>
      <EntryFields c={c} f={f} setF={setF} member={member} />
      <SubmitBtn busy={busy} label={`See my ${c.quiz_kind === "trivia" ? "score" : "result"}`} testid="quiz-submit" />
    </form>
  );
}

function Ballot({ c }) {
  const [votes, setVotes] = useState({});
  const [f, setF, member] = useEntrant();
  const [run, busy, res] = useSubmit((d) => enterCampaign(c.id, d));
  if (res) return <ThankYou res={res} />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); run({ ...f, votes }); }} className="space-y-5" data-testid="ballot-form">
      {(c.categories || []).map((cat) => (
        <div key={cat.id} className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
          <p className="font-portal text-lg font-extrabold text-[var(--p-ink)]">{cat.name}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {cat.nominees.map((n) => (
              <button type="button" key={n.id} onClick={() => setVotes({ ...votes, [cat.id]: n.id })} data-testid={`ballot-nominee-${n.id}`}
                className={`rounded-xl px-4 py-3 text-left text-sm font-semibold ring-1 ${votes[cat.id] === n.id ? "bg-[var(--p-teal)] text-white ring-[var(--p-teal)]" : "bg-white text-[var(--p-ink)] ring-slate-200 hover:ring-[var(--p-teal)]"}`}>{n.name}</button>
            ))}
          </div>
        </div>
      ))}
      <div className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200">
        <h2 className="font-portal text-xl font-extrabold text-[var(--p-ink)]">Cast your ballot <span className="text-xs font-semibold text-slate-400">One ballot per day</span></h2>
        <EntryFields c={c} f={f} setF={setF} member={member} />
        <SubmitBtn busy={busy} label="Submit my ballot" testid="ballot-submit" />
      </div>
    </form>
  );
}

function PhotoContest({ c, reload }) {
  const [f, setF, member] = useEntrant();
  const [photo, setPhoto] = useState({ image: "", caption: "" });
  const [voter, setVoter] = useState(localStorage.getItem("nl_voter_email") || "");
  const [run, busy, res, setRes] = useSubmit((d) => submitContestPhoto(c.id, d));
  const vote = async (p) => {
    const email = voter || member?.email || window.prompt("Enter your email to vote (one vote per day):");
    if (!email) return;
    localStorage.setItem("nl_voter_email", email); setVoter(email);
    try { await voteContestPhoto(c.id, p.id, email); toast.success("Vote counted!"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="photo-gallery">
        {(c.photos || []).map((p) => (
          <figure key={p.id} data-testid={`contest-photo-${p.id}`} className="overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
            <img src={p.image} alt={p.caption} className="h-52 w-full object-cover object-top" />
            <figcaption className="flex items-center justify-between gap-2 p-3">
              <div className="min-w-0"><p className="truncate text-sm font-semibold text-[var(--p-ink)]">{p.caption}</p><p className="text-xs text-slate-400">{p.name}</p></div>
              <button onClick={() => vote(p)} data-testid={`vote-photo-${p.id}`} className="flex shrink-0 items-center gap-1 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-600 hover:bg-rose-100"><Heart size={13} /> {p.votes}</button>
            </figcaption>
          </figure>
        ))}
      </div>
      {res ? <ThankYou res={res} onAgain={() => setRes(null)} /> : (
        <form onSubmit={(e) => { e.preventDefault(); run({ ...f, ...photo }); }} className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="photo-entry-form">
          <h2 className="font-portal text-xl font-extrabold text-[var(--p-ink)]">Upload your entry</h2>
          <ImageUpload value={photo.image} onChange={(url) => setPhoto((p) => ({ ...p, image: url }))} testid="contest-photo-upload" camera={false} />
          <input data-testid="contest-photo-caption" placeholder="Caption" value={photo.caption} onChange={(e) => setPhoto({ ...photo, caption: e.target.value })} className={pInput} />
          <EntryFields c={c} f={f} setF={setF} member={member} />
          <SubmitBtn busy={busy} label="Submit my photo" testid="photo-entry-submit" />
        </form>
      )}
    </div>
  );
}
